/**
 * Customer Registration Wizard — thin orchestrator.
 * Step bodies live in /wizard/customer/, shared shell in /wizard/WizardShell.jsx.
 */
import { useMemo, useState } from 'react';
import { controlApi } from '@/services/api';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, User, Globe, CheckCircle,
  Factory, Truck, Store, Hospital, Package, Settings, Activity,
} from 'lucide-react';
import { WizardShell } from './wizard/WizardShell';
import { CustomerStep1Type } from './wizard/customer/CustomerStep1Type';
import { CustomerStep2Company } from './wizard/customer/CustomerStep2Company';
import { CustomerStep3Admin } from './wizard/customer/CustomerStep3Admin';
import { CustomerStep4Review } from './wizard/customer/CustomerStep4Review';
import { CustomerStepTelemetry, PROFILE_PRESETS } from './wizard/customer/CustomerStepTelemetry';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CUSTOMER_TYPES = [
  { id: 'manufacturer', label: 'Pharmaceutical Manufacturer', icon: Factory, description: 'Produces pharmaceutical products' },
  { id: 'distributor', label: 'Distributor / Wholesaler', icon: Truck, description: 'Distributes to retailers' },
  { id: 'retailer', label: 'Pharmacy / Retailer', icon: Store, description: 'Sells to end consumers' },
  { id: 'hospital', label: 'Hospital / Healthcare', icon: Hospital, description: 'Healthcare facility' },
  { id: 'logistics', label: 'Logistics Provider', icon: Package, description: '3PL logistics company' },
  { id: 'other', label: 'Other', icon: Settings, description: 'Custom business type' },
];

const STEPS = [
  { id: 1, title: 'Customer Type', icon: Building2, description: 'Select business type' },
  { id: 2, title: 'Company Info', icon: Globe, description: 'Organization details' },
  { id: 3, title: 'Telemetry', icon: Activity, description: 'Channels & profile' },
  { id: 4, title: 'Admin User', icon: User, description: 'Primary contact' },
  { id: 5, title: 'Review', icon: CheckCircle, description: 'Confirm & create' },
];

const BLANK = {
  customer_type: '',
  custom_type: '',
  company_name: '',
  company_code: '',
  address: '',
  city: '',
  state: '',
  country: 'India',
  phone: '',
  website: '',
  admin_name: '',
  admin_email: '',
  admin_phone: '',
  admin_password: '',
  // Telemetry defaults — PHARMA profile feels right for most signups
  profile_type: 'PHARMA',
  telemetry_channels: { ...PROFILE_PRESETS.PHARMA },
};

const STEP_VALIDATORS = {
  1: (data) => {
    if (!data.customer_type) return 'Please select a customer type';
    if (data.customer_type === 'other' && !data.custom_type.trim()) return 'Please specify the custom business type';
    return null;
  },
  2: (data) => {
    if (!data.company_name.trim()) return 'Company name is required';
    if (!data.company_code.trim()) return 'Company code is required';
    return null;
  },
  3: (data) => {
    if (!data.profile_type) return 'Please select a customer profile';
    const ch = data.telemetry_channels || {};
    if (!Object.values(ch).some(Boolean)) return 'Enable at least one telemetry channel';
    return null;
  },
  4: (data) => {
    if (!data.admin_name.trim()) return 'Admin name is required';
    if (!data.admin_email.trim() || !data.admin_email.includes('@')) return 'Valid admin email is required';
    if (!data.admin_password || data.admin_password.length < 6) return 'Password must be at least 6 characters';
    return null;
  },
};

export default function CustomerRegistrationWizard({ isOpen, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(BLANK);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // Auto-derive company_code from name on first edit
      if (field === 'company_name' && !prev.company_code) {
        next.company_code = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
      }
      return next;
    });
  };

  const handleNext = () => {
    const validator = STEP_VALIDATORS[step];
    const err = validator ? validator(formData) : null;
    if (err) {
      toast.error(err);
      return;
    }
    setStep((prev) => Math.min(prev + 1, STEPS.length));
  };

  const handleBack = () => setStep((prev) => Math.max(prev - 1, 1));

  const handleReset = () => {
    setStep(1);
    setFormData(BLANK);
    onClose?.();
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const customerType = formData.customer_type === 'other'
        ? formData.custom_type
        : CUSTOMER_TYPES.find((t) => t.id === formData.customer_type)?.label || formData.customer_type;

      const tenantRes = await controlApi.createTenant({
        name: formData.company_name,
        code: formData.company_code.toUpperCase(),
        customer_type: customerType,
        address: formData.address || null,
        city: formData.city || null,
        state: formData.state || null,
        country: formData.country || 'India',
        phone: formData.phone || null,
        website: formData.website || null,
        admin_user: {
          full_name: formData.admin_name,
          email: formData.admin_email,
          phone: formData.admin_phone || null,
          password: formData.admin_password,
        },
        status: 'active',
      });

      // Apply telemetry config on the new tenant. Tenant API does not yet
      // accept these inline, so we PUT them right after creation.
      const newTenantId = tenantRes.data?.id;
      if (newTenantId) {
        try {
          await axios.put(`${API_BASE}/api/v1/control/tenants/${newTenantId}/telemetry-config`, {
            profile_type: formData.profile_type,
            channels: formData.telemetry_channels,
          });
        } catch (configErr) {
          // Non-fatal: tenant is created; surface as a warning.
          toast.warning('Customer created — but telemetry config could not be saved. Edit it from the tenant page.');
        }
      }

      toast.success('Customer registered successfully!');
      onSuccess?.();
      handleReset();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register customer');
    } finally {
      setLoading(false);
    }
  };

  const selectedType = useMemo(
    () => CUSTOMER_TYPES.find((t) => t.id === formData.customer_type),
    [formData.customer_type],
  );

  if (!isOpen) return null;

  return (
    <WizardShell
      title="Register New Customer"
      steps={STEPS}
      step={step}
      loading={loading}
      onClose={handleReset}
      onBack={handleBack}
      onNext={handleNext}
      onSubmit={handleSubmit}
      submitLabel="Create Customer"
      submittingLabel="Creating..."
      backTestId="customer-wizard-back-btn"
      nextTestId="customer-wizard-next-btn"
      submitTestId="customer-wizard-submit-btn"
    >
      {step === 1 && (
        <CustomerStep1Type formData={formData} onChange={handleChange} types={CUSTOMER_TYPES} />
      )}
      {step === 2 && <CustomerStep2Company formData={formData} onChange={handleChange} />}
      {step === 3 && <CustomerStepTelemetry formData={formData} onChange={handleChange} />}
      {step === 4 && <CustomerStep3Admin formData={formData} onChange={handleChange} />}
      {step === 5 && <CustomerStep4Review formData={formData} selectedType={selectedType} />}
    </WizardShell>
  );
}
